import { FeedDispatchContext, FeedStateContext } from "context/feed";
import React, { useEffect, useState } from "react";
import WelcomeMessage, {
  WELCOME_MESSAGE_PROFILE,
} from "components/WelcomeMessage";
import { feedInitialState, feedReducer } from "context/feed";

import {
  AUDIENCE_TYPES,
  AUDIENCE_TYPES_ARRAY,
  getAudienceEmoji,
  getAudienceSlug,
} from "posts/constants";
import AboutCard from "components/ProfileCards/AboutCard";
import AgeCard from "components/ProfileCards/AgeCard";
import AudienceEmoji from "components/AudienceEmoji";
import BrandsCard from "components/ProfileCards/BrandsCard";
import Button from "components/Button";
import { Modal as BootstrapModal } from "react-bootstrap";
import EducationCard from "components/ProfileCards/EducationCard";
import { FullWidthContainer } from "components/PageContainer";
import GalleryCard from "components/ProfileCards/GalleryCard";
import GenderCard from "components/ProfileCards/GenderCard";
import Head from "next/head";
import IncomeCard from "components/ProfileCards/IncomeCard";
import InterestsCard from "components/ProfileCards/InterestsCard";
import LocationCard from "components/ProfileCards/LocationCard";
import Masonry from "react-masonry-css";
import NarrativesCard from "components/ProfileCards/NarrativesCard";
import NewsSitesCard from "components/ProfileCards/NewsSitesCard";
import PlaylistCard from "components/ProfileCards/PlaylistCard";
import PoliticalPartyCard from "components/ProfileCards/PoliticalPartyCard";
import Nav from "components/Nav/Nav";
import ProfileBanner from "components/ProfileBanner";
import RaceCard from "components/ProfileCards/RaceCard";
import ReligiosityCard from "components/ProfileCards/ReligiosityCard";
import SecondaryNav from "components/Nav/SecondaryNav";
import ShoppingListCard from "components/ProfileCards/ShoppingListCard";
import ValuesCard from "components/ProfileCards/ValuesCard";
import VoicesCard from "components/ProfileCards/VoicesCard";
import ZeroState from "components/ZeroState";
import isEqual from "lodash.isequal";
import styled from "styled-components";
import { useReducer } from "react";
import AudienceMapCard from "components/ProfileCards/AudienceMapCard";
import TopYoutubeCard from "components/ProfileCards/TopYoutubeCard";
import ReachGraph from "components/ProfileCards/ReachGraph";
import StickinessGraph from "components/ProfileCards/StickinessGraph";
import { InView } from "react-intersection-observer";
import { getAudienceTypeColor } from "utils/styles";
import {
  SecondaryMenuWrapper,
  SecondaryMenuButton,
  SecondaryMenuGroup,
} from "components/Nav/components";
import FlexSpacer from "components/FlexSpacer";
import Link from "next/link";

const KEYCODE_LEFT_ARROW = 37;
const KEYCODE_RIGHT_ARROW = 39;
const BREAKPOINT_TWO_COL = 800;

const CardGrid = styled(Masonry)`
  display: flex;
  width: auto;
  max-width: 80rem;
  margin: 0 auto;

  .masonry-column {
    background-clip: padding-box;

    @media (min-width: ${BREAKPOINT_TWO_COL}px) {
      &:nth-child(2n) {
        margin-left: 1rem; /* gutter size */
      }
    }
  }
`;

const Container = styled(FullWidthContainer)`
  margin: 1rem auto;
`;

const SectionHeading = styled.h2.attrs(() => ({
  className: "mabry-medium",
}))`
  margin: auto;
  margin-top: 10rem;
  margin-bottom: 50px;
  max-width: 80rem;
  font-size: 35pt;
  padding-left: 1rem;
`;

const Modal = styled(BootstrapModal)`
  .modal-content {
    background: ${({ audience }) => {
      return getAudienceTypeColor(audience);
    }};
  }

  .modal-header {
    font-family: "mabry-regular", sans-serif;
    font-size: 1.125rem;
  }
`;

export default function Profile({
  currentAudience,
  baseUrl,
  showAudienceGrid,
}) {
  const [activeSectionName, setActiveSectionName] = useState("profile");

  const [state, dispatch] = useReducer(feedReducer, feedInitialState);

  useEffect(() => {
    dispatch({
      type: "set-audience",
      payload: currentAudience,
    });
  }, [currentAudience]);

  const [showQuizResultsModal, setShowQuizResultsModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("quiz")) {
      setShowQuizResultsModal(true);
    }
  }, []);

  const updateActiveAudience = (audience) => {
    if (!isEqual(audience, state.audience)) {
      dispatch({
        type: "set-audience",
        payload: audience,
      });
      switch (audience) {
        case AUDIENCE_TYPES.PEOPLE_POWER:
          window.history.replaceState({}, null, "/audiences/people-power");
          break;
        case AUDIENCE_TYPES.IF_YOU_SAY_SO:
          window.history.replaceState({}, null, "/audiences/if-you-say-so");
          break;
        case AUDIENCE_TYPES.TOUGH_COOKIES:
          window.history.replaceState({}, null, "/audiences/tough-cookies");
          break;
        case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
          window.history.replaceState({}, null, "/audiences/dont-tread-on-me");
          break;
      }
    }
  };

  useEffect(() => {
    // On page load, we first want to pull audience/narrative state form the
    // url. If it doesn't exist there, (i.e. this is someone's first visit from
    // a direct link to this page), intiialize the state with no filters (and
    // we can either show everything or nothing).
    const params = new URLSearchParams(window.location.search);
    if (params.has("audience")) {
      const a = params.get("audience");
      if (a.length) {
        updateActiveAudience(a);
      } else {
        updateActiveAudience();
      }
    }

    // Remove any query params from the URL after we have cached them
    setTimeout(() => {
      window.history.replaceState({}, null, window.location.href.split("?")[0]);
    }, 10);
  }, []);

  const renderQuizResultsModalContent = () => {
    switch (currentAudience) {
      case AUDIENCE_TYPES.PEOPLE_POWER:
        return (
          <>
            <p>
              You are <strong>People Power</strong>. You know that the system is
              broken, but real change is possible if people come together.
            </p>

            <p>
              Community-minded, politically engaged, and ready to fight for
              system reform to solve social issues like poverty.
            </p>

            <p>
              About half people of color and slightly more women than men,{" "}
              <strong>People Power</strong> is super-secular and
              super-progressive.
            </p>
          </>
        );
      case AUDIENCE_TYPES.IF_YOU_SAY_SO:
        return (
          <>
            <p>
              You are <strong>If You Say So</strong>. You want things to be
              better, but you are a realist. You know people can suck, and the
              system is broken. The system might suck, but there are plenty of
              places to find a little fun.
            </p>

            <p>
              <strong>If You Say So</strong> tends to be skeptical that there
              are any solutions to society’s big problems, but they aren’t
              letting that stop them having a good time and working toward their
              dreams.
            </p>

            <p>
              About half men, half people of color, a little over half
              Democrats, <strong>If You Say So</strong> stands out more for
              their cultural profile than for their demographics.
            </p>
          </>
        );
      case AUDIENCE_TYPES.TOUGH_COOKIES:
        return (
          <>
            <p>
              You are a <strong>Tough Cookie</strong>. You know racism is real,
              and the world is not fair. But the best way to get ahead is to
              keep working at it.
            </p>
            <p>
              <strong>Tough Cookies</strong> tend to put family first and follow
              the rules. They believe that while the system might be broken,
              hard work can create success.
            </p>
            <p>
              Racially and politically diverse—about a third identify as
              Democrat, Republican, and Independent, respectively—{" "}
              <strong>Tough Cookies</strong> are the oldest group. More than
              half are over 55.
            </p>
          </>
        );
      case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
        return (
          <>
            <p>
              You are <strong>Don’t Tread on Me</strong>. You go after what you
              want and don’t ask for anything you haven’t earned.
            </p>

            <p>
              <strong>Don’t Tread on Me</strong> believes that equal opportunity
              already exists in America for anyone willing to work for it.
            </p>

            <p>
              Majority white and majority Republican,{" "}
              <strong>Don’t Tread on Me</strong> earns more at work than any
              other audience.
            </p>
          </>
        );
    }
  };

  const handleChangeAudience = (delta) => {
    const currentIndex = AUDIENCE_TYPES_ARRAY.indexOf(state.audience);
    let newIndex = (currentIndex + delta) % AUDIENCE_TYPES_ARRAY.length;
    if (newIndex < 0) {
      newIndex = AUDIENCE_TYPES_ARRAY.length + newIndex;
    }
    updateActiveAudience(AUDIENCE_TYPES_ARRAY[newIndex]);
  };

  const handleWindowKeydown = (e) => {
    if (e.keyCode === KEYCODE_LEFT_ARROW) {
      handleChangeAudience(-1);
    } else if (e.keyCode === KEYCODE_RIGHT_ARROW) {
      handleChangeAudience(1);
    }
  };

  const audienceColor = getAudienceTypeColor(state.audience);

  useEffect(() => {
    window.addEventListener("keydown", handleWindowKeydown);
    return () => {
      window.removeEventListener("keydown", handleWindowKeydown);
    };
  }, [state.audience]);

  const cardGridProps = {
    breakpointCols: {
      default: 2,
      [BREAKPOINT_TWO_COL]: 1,
    },
    columnClassName: "masonry-column",
  };

  const getOgImage = (currentAudience) => {
    switch (currentAudience) {
      case AUDIENCE_TYPES.PEOPLE_POWER:
        return "open-graph-people-power.png";
      case AUDIENCE_TYPES.IF_YOU_SAY_SO:
        return "open-graph-if-you-say-so.png";
      case AUDIENCE_TYPES.TOUGH_COOKIES:
        return "open-graph-tough-cookies.png";
      case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
        return "open-graph-dont-tread-on-me.png";
      default:
        return "open-graph.png";
    }
  };

  if (!showAudienceGrid && (!state.audience || currentAudience === null))
    return null;

  return (
    <FeedStateContext.Provider value={state}>
      <FeedDispatchContext.Provider value={dispatch}>
        <div>
          <Head>
            <title>
              Audience Profile
              {state && state.audience ? ` | ${state.audience}` : ""}
            </title>
            <link rel="icon" href="/favcon2.png" />
            <meta
              property="og:image"
              content={`${baseUrl}/images/${getOgImage(currentAudience)}`}
            />
            <meta
              property="twitter:image"
              content={`${baseUrl}/images/${getOgImage(currentAudience)}`}
            />
          </Head>

          <main>
            <Nav
              profileIsActive
              activeSection={activeSectionName}
              highlightColor={audienceColor}
              breadCrumbs={[
                <Link href="/audiences">Audiences</Link>,
                <a href={`/audiences/${getAudienceSlug(state.audience)}`}>
                  <span>
                    {getAudienceEmoji(state.audience)}&nbsp;{state.audience}
                  </span>
                </a>,
              ]}
              secondaryNav={
                <SecondaryMenuWrapper>
                  <FlexSpacer />
                  <SecondaryMenuGroup>
                    <SecondaryMenuButton
                      href="#profile"
                      text="Profile"
                      icon="info"
                    />
                    <SecondaryMenuButton
                      href="#demographics"
                      text="Demographics"
                      icon="map"
                    />
                    <SecondaryMenuButton
                      href="#media"
                      text="Media"
                      icon="image"
                    />
                  </SecondaryMenuGroup>
                </SecondaryMenuWrapper>
              }
            />
            {showAudienceGrid ? (
              <>
                <Container>
                  <ZeroState
                    title="Select an Audience to Explore their Profile."
                    urlPrefix="/audiences"
                    buttonText="Explore the Audiences"
                  />
                </Container>
              </>
            ) : (
              <>
                <WelcomeMessage
                  activeAudience={state.audience}
                  type={WELCOME_MESSAGE_PROFILE}
                />
                <a name="profile"></a>
                <ProfileBanner
                  activeAudience={state.audience}
                  onChangeAudience={handleChangeAudience}
                />
                <Container>
                  <InView
                    as="div"
                    initialInView={true}
                    id="profile_profile"
                    rootMargin="-35%"
                    onChange={(inView) => {
                      inView && setActiveSectionName("profile");
                    }}
                  >
                    <CardGrid {...cardGridProps}>
                      <AboutCard activeAudience={state.audience} />
                      <ValuesCard activeAudience={state.audience} />
                      <GalleryCard activeAudience={state.audience} />
                      <NarrativesCard activeAudience={state.audience} />
                      <InterestsCard activeAudience={state.audience} />
                      <AudienceMapCard activeAudience={state.audience} />
                    </CardGrid>
                  </InView>

                  <InView
                    as="div"
                    rootMargin="-35%"
                    onChange={(inView) => {
                      inView && setActiveSectionName("demographics");
                    }}
                  >
                    <a name="demographics"></a>
                    <SectionHeading id="profile_demographics">
                      Demographics
                    </SectionHeading>
                    <CardGrid {...cardGridProps}>
                      <AgeCard activeAudience={state.audience} />
                      <GenderCard activeAudience={state.audience} />
                      <EducationCard activeAudience={state.audience} />
                      <ReligiosityCard activeAudience={state.audience} />
                      <IncomeCard activeAudience={state.audience} />
                      <RaceCard activeAudience={state.audience} />
                      <PoliticalPartyCard activeAudience={state.audience} />
                    </CardGrid>
                  </InView>
                  <InView
                    as="div"
                    rootMargin="-35%"
                    onChange={(inView) => {
                      inView && setActiveSectionName("media");
                    }}
                  >
                    <a name="media"></a>
                    <SectionHeading id="profile_media">Media</SectionHeading>
                    <CardGrid {...cardGridProps}>
                      <PlaylistCard activeAudience={state.audience} />
                      <VoicesCard activeAudience={state.audience} />
                      <BrandsCard activeAudience={state.audience} />
                      <NewsSitesCard activeAudience={state.audience} />
                      <ShoppingListCard activeAudience={state.audience} />
                    </CardGrid>
                  </InView>
                </Container>
              </>
            )}
          </main>
          <Modal
            centered
            audience={currentAudience}
            show={showQuizResultsModal}
            onHide={() => {
              setShowQuizResultsModal(false);
            }}
          >
            <Modal.Header closeButton>
              <AudienceEmoji audience={currentAudience} />
              {currentAudience}
            </Modal.Header>
            <Modal.Body>{renderQuizResultsModalContent()}</Modal.Body>
          </Modal>
          <SecondaryNav />
        </div>
      </FeedDispatchContext.Provider>
    </FeedStateContext.Provider>
  );
}
